<template lang="pug">
    div
        // Skeleton state — shows while the initial feed request is in
        // flight. Layout mirrors a real .feed row so content doesn't
        // jump when items load in.
        v-layout(v-if="loading && !allFeed.length" row wrap)
            v-flex(xs12 v-for="n in skeletonCount" :key="`feed-skel-${n}`")
                div.feedSkeleton
                    div.feedSkeleton__image.shimmer
                    div.feedSkeleton__body
                        div.feedSkeleton__line.feedSkeleton__line--long.shimmer
                        div.feedSkeleton__line.feedSkeleton__line--short.shimmer

        v-layout(v-else row wrap)
            v-flex(xs12 v-for="(item, key) in visibleFeed" :key="`feed-${item.id}`")
                feed-item(:item="item")

        div.loadMore(v-if="paginated && hasMore")
            v-btn(
                flat
                color="white"
                :loading="loading"
                @click="loadMore"
            ) Load more
</template>

<script>
    import sessions from '~api/sessions';
    import FeedItem from './FeedItem';
    import mindfulMinute from '~api/mindfulMinute';
    import dayjs from 'dayjs';
    import VueTypes from 'vue-types';

    const PAGE_SIZE = 8;
    // Fetch a large batch upfront so all pages are available client-side without extra requests
    const FETCH_LIMIT = 48;

    export default {
        components: {
            FeedItem
        },
        props: {
            paginated: VueTypes.bool.def(false)
        },
        data() {
            return {
                allFeed: [],
                page: 1,
                loading: false
            };
        },
        computed: {
            visibleFeed() {
                if (!this.paginated) return this.allFeed;
                return this.allFeed.slice(0, this.page * PAGE_SIZE);
            },

            hasMore() {
                return this.visibleFeed.length < this.allFeed.length;
            },

            // How many skeleton rows to show while loading. Mirrors the
            // page/viewport logic so there's no visual jump when real
            // data arrives.
            skeletonCount() {
                if (this.paginated) return PAGE_SIZE;
                return this.$vuetify.breakpoint.lgAndDown ? 4 : 6;
            }
        },
        async created() {
            this.loading = true;
            try {
                const { data: dataMinutes } = await mindfulMinute.list();
                const { data: dataSessions } = await sessions.list({
                    type: 'recorded',
                    sortBy: 'updatedAt',
                    orderBy: 'desc',
                    limit: FETCH_LIMIT
                });
                const list = [
                    ...dataMinutes?.items,
                    ...dataSessions?.items.filter(item => item.theme !== 'live')
                ];
                list.sort((a, b) =>
                    dayjs(b.updatedAt).diff(dayjs(a.updatedAt))
                );

                if (this.paginated) {
                    this.allFeed = list;
                } else {
                    this.allFeed = list.slice(
                        0,
                        this.$vuetify.breakpoint.lgAndDown ? 12 : 8
                    );
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        methods: {
            loadMore() {
                this.page++;
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../../../assets/style/_variables.styl";

    .loadMore
        display: flex
        justify-content: center
        padding: 8px 0 16px

    // Skeleton loader — matches the .feed row geometry in FeedItem.vue
    // so swapping placeholders for real content doesn't shift layout.
    .feedSkeleton
        background: linear-gradient(90deg, rgba(255,255,255, 0.06) 0%, transparent 100%)
        border-radius: 10px
        margin: 8px 0
        display: flex
        align-items: center

    .feedSkeleton__image
        width: 220px
        height: 175px
        border-radius: 10px
        flex: 0 0 auto

    .feedSkeleton__body
        flex: 1 1 auto
        padding: 16px 24px
        display: flex
        flex-direction: column
        gap: 10px

    .feedSkeleton__line
        height: 18px
        border-radius: 6px

    .feedSkeleton__line--long
        width: 80%

    .feedSkeleton__line--short
        width: 45%

    // Shimmer pass: a soft moving highlight sweeping across each grey
    // block. Matches Material / iOS system skeletons and tells the user
    // "this is loading, not just a grey tile."
    .shimmer
        position: relative
        overflow: hidden
        background-color: rgba(255, 255, 255, 0.08)

        &:after
            content: ''
            position: absolute
            top: 0
            left: 0
            right: 0
            bottom: 0
            transform: translateX(-100%)
            background: linear-gradient(
                90deg,
                transparent,
                rgba(255, 255, 255, 0.12),
                transparent
            )
            animation: shimmer 1.4s infinite

    @keyframes shimmer
        100%
            transform: translateX(100%)

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .feedSkeleton__image
            width: 140px
            height: 95px
        .feedSkeleton__body
            padding: 8px 16px
            gap: 8px
        .feedSkeleton__line
            height: 14px
</style>
